/**
 * Unified hooks for getting entity IDs from user IDs
 *
 * These hooks resolve entity IDs via `/auth/entity-id` (relation-first linking).
 * When no link exists, `data` is null—callers must handle missing entity context.
 */

import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '../../store';
import { api } from '../../services/api';

/**
 * Get tenant ID for the current user
 */
export const useTenantIdFromUserId = (userId: string | null, userRole: string | null) => {
  return useQuery({
    queryKey: ['tenantId', userId, userRole],
    queryFn: async () => {
      if (!userId || !userRole) return null;
      return await api.auth.getEntityId(userId, userRole);
    },
    enabled: !!userId && !!userRole && userRole === 'TENANT',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
};

/**
 * Get owner ID for the current user
 */
export const useOwnerIdFromUserId = (userId: string | null, userRole: string | null) => {
  return useQuery({
    queryKey: ['ownerId', userId, userRole],
    queryFn: async () => {
      if (!userId || !userRole) return null;
      return await api.auth.getEntityId(userId, userRole);
    },
    enabled: !!userId && !!userRole && userRole === 'OWNER',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
};

/**
 * Get vendor ID for the current user
 */
export const useVendorIdFromUserId = (userId: string | null, userRole: string | null) => {
  return useQuery({
    queryKey: ['vendorId', userId, userRole],
    queryFn: async () => {
      if (!userId || !userRole) return null;
      return await api.auth.getEntityId(userId, userRole);
    },
    enabled: !!userId && !!userRole && userRole === 'VENDOR',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
};

/**
 * Get investor ID for the current user
 */
export const useInvestorIdFromUserId = (userId: string | null, userRole: string | null) => {
  return useQuery({
    queryKey: ['investorId', userId, userRole],
    queryFn: async () => {
      if (!userId || !userRole) return null;
      return await api.auth.getEntityId(userId, userRole);
    },
    enabled: !!userId && !!userRole && userRole === 'INVESTOR',
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });
};

/**
 * Get tenant ID for current authenticated user
 * Convenience hook that reads userId and userRole from store
 */
export const useCurrentTenantId = () => {
  const userId = useAppStore(state => state.currentUserId);
  const userRole = useAppStore(state => state.userRole);
  
  const queryResult = useTenantIdFromUserId(userId, userRole);
  
  return {
    ...queryResult,
    data: queryResult.data ?? null,
  };
};

/**
 * Get owner ID for current authenticated user
 * Convenience hook that reads userId and userRole from store
 */
export const useCurrentOwnerId = () => {
  const userId = useAppStore(state => state.currentUserId);
  const userRole = useAppStore(state => state.userRole);
  
  const queryResult = useOwnerIdFromUserId(userId, userRole);
  
  return {
    ...queryResult,
    data: queryResult.data ?? null,
  };
};

/**
 * Get vendor ID for current authenticated user
 * Convenience hook that reads userId and userRole from store
 */
export const useCurrentVendorId = () => {
  const userId = useAppStore(state => state.currentUserId);
  const userRole = useAppStore(state => state.userRole);
  
  const queryResult = useVendorIdFromUserId(userId, userRole);
  
  return {
    ...queryResult,
    data: queryResult.data ?? null,
  };
};

/**
 * Get investor ID for current authenticated user
 * Convenience hook that reads userId and userRole from store
 */
export const useCurrentInvestorId = () => {
  const userId = useAppStore(state => state.currentUserId);
  const userRole = useAppStore(state => state.userRole);
  
  const queryResult = useInvestorIdFromUserId(userId, userRole);
  
  return {
    ...queryResult,
    data: queryResult.data ?? null,
  };
};
